import React from "react"
import { Box, Button, Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material"
import { TRIPLE_PRODUCTS } from "src/modules/const"
import { Company, ProductPermission } from "src/openapi"
import "./index.scss"
import "./permission_table.scss"
import { useTranslation } from "react-i18next"
import AppModal from "src/components/atom/AppModal"
import { formatDate } from "src/modules/convert"

interface Props {
  company?: Company
  onCreatePermission(productName: string): void
  onDeletePermission(permissionId: number): void
}

export default function PermissionTable({
  company,
  onCreatePermission,
  onDeletePermission,
}: Props) {
  const { t } = useTranslation("translation", { keyPrefix: "company" })

  const productToPermission = new Map(
    company?.productPermissions?.map((p) => [p.productName, p]) || []
  )

  const [createProductPermissionModalOpen, setCreateProductPermissionModalOpen] =
    React.useState<boolean>(false)
  const [targetProduct, setTargetProduct] = React.useState<string>()
  const handleOpenCreateProductPermissionModal = (productName: string) => () => {
    setTargetProduct(productName)
    setCreateProductPermissionModalOpen(true)
  }

  const [targetPermission, setTargetPermission] = React.useState<ProductPermission>()
  const [deleteProductPermissionModalOpen, setDeleteProductPermissionModalOpen] =
    React.useState<boolean>(false)
  const handleDeleteProductPermissionModalOpen = (permission: ProductPermission) => () => {
    setTargetPermission(permission)
    setDeleteProductPermissionModalOpen(true)
  }

  return (
    <>
      <Box className="company-content-container">
        <Table className="product-permission-table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "20%" }}>{t("product_name")}</TableCell>
              <TableCell sx={{ width: "20%" }}>{t("start_date")}</TableCell>
              <TableCell sx={{ width: "15%" }}>{t("permission_status")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {TRIPLE_PRODUCTS.map((product) => (
              <TableRow key={`row_${product}`}>
                <TableCell>{product}</TableCell>
                <TableCell>
                  {productToPermission.get(product)
                    ? formatDate(productToPermission.get(product)!.createdAt)
                    : "-"}
                </TableCell>
                <TableCell>
                  {productToPermission.get(product) ? (
                    <Button
                      variant="text"
                      color="secondary"
                      onClick={handleDeleteProductPermissionModalOpen(
                        productToPermission.get(product)!
                      )}
                    >
                      {t("permission_status_active")}
                    </Button>
                  ) : (
                    <Button
                      variant="text"
                      color="error"
                      onClick={handleOpenCreateProductPermissionModal(product)}
                    >
                      {t("permission_status_inactive")}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <AppModal
        isOpen={createProductPermissionModalOpen}
        onClose={() => setCreateProductPermissionModalOpen(false)}
        title={`${t("create_permission")}: ${targetProduct}`}
        description={t("create_permission_confirm")}
        confirmText={t("create_permission")}
        cancelText={t("cancel")}
        onConfirm={() => {
          onCreatePermission(targetProduct!)
          setCreateProductPermissionModalOpen(false)
        }}
        onCancel={() => setCreateProductPermissionModalOpen(false)}
      />
      <AppModal
        isOpen={deleteProductPermissionModalOpen}
        onClose={() => setDeleteProductPermissionModalOpen(false)}
        title={`${t("delete_permission")}: ${targetPermission?.productName}`}
        description={t("delete_permission_confirm")}
        confirmText={t("delete_permission")}
        cancelText={t("cancel")}
        onConfirm={() => {
          onDeletePermission(targetPermission!.id!)
          setDeleteProductPermissionModalOpen(false)
        }}
        onCancel={() => setDeleteProductPermissionModalOpen(false)}
      />
    </>
  )
}
